import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { useDispatch } from 'react-redux'
import { Box, Grid } from '@mui/material'
import { isNil, keys } from 'lodash'
import { useParams } from 'react-router'
import NumberField from '../../../../components/forms/NumberField'
import Select from '../../../../components/forms/Select'
import { CardTable } from '../../../../components/datatable/NewTable'
import { CardTitle } from '../../../../components/card/NewCard'
import useActions from '../../../../utils/customHook/useActions'
import useBoolean from '../../../../utils/customHook/useBoolean'
import useSandreList from '../../../../utils/customHook/useSandreList'
import OperationAction from '../../operation/actions/OperationAction'
import ToastrAction from '../../../../toastr/actions/ToastrAction'
import { darkBlue, mainBlue } from '../../../../utils/constants/ColorTheme'

const PERIPHYTON = 'PERIPHYTON'
const FACIES = 'PRELEVEMENTS.FACIES'

const UR1 = 1
const UR2 = 2

const GROUP_HEADERS = [
    { key: 'label', value: ' ', noSort: true },
    { key: 'ur1', value: 'UR1', noSort: true, width: 120 },
    { key: 'ur2', value: 'UR2', noSort: true, width: 120 },
]
const NO_PAGINATION = [{ value: 1000, label: '1000' }]

const cellKey = (group, subGroup, unit) => `${group}:${subGroup}:${unit}`

const HydrobioOperationSurveyUnits = () => {
    const dispatch = useDispatch()
    const { id: idStation, idOperation } = useParams()

    const periphytonList = useSandreList(PERIPHYTON)
    const faciesList = useSandreList(FACIES)
    const sandreLists = useMemo(() => ({ [PERIPHYTON]: periphytonList, [FACIES]: faciesList }), [periphytonList, faciesList])

    const faciesGroup = {
        group: 0,
        title: i18n.faciesTitle,
        rows: [
            { label: i18n.dominantFacies, sandre: FACIES },
            { label: i18n.urCoverageRatePerSamplingPoint, percent: true },
            { label: i18n.totalVegetalSurfacePerUr, percent: true },
            { label: i18n.periphyton, sandre: PERIPHYTON },
            { label: i18n.heterotrophicRate, percent: true },
            { label: i18n.algaeRate, percent: true },
            { label: i18n.bryophytesRate, percent: true },
            { label: i18n.pteridophytesLichensRate, percent: true },
            { label: i18n.phanerogamsRate, percent: true },
            { label: i18n.floatingVegetationRate, percent: true },
            { label: i18n.submergedVegetationRate, percent: true },
        ],
    }

    const urGroups = [
        {
            group: 1,
            title: i18n.characteristics,
            rows: [
                { label: i18n.urCoverageRate, percent: true },
                { label: i18n.urLength },
                { label: i18n.urWidth },
                { label: i18n.vegetatedSurfaceRate, percent: true },
                { label: i18n.periphyton, sandre: PERIPHYTON },
            ],
        },
        {
            group: 2,
            title: i18n.faciesType,
            rows: [
                { label: i18n.channelLentic },
                { label: i18n.plateLentic },
                { label: i18n.pool },
                { label: i18n.dissipationPit },
                { label: i18n.channelLotic },
                { label: i18n.riffle },
                { label: i18n.cascade },
                { label: i18n.plateFlowing, subGroup: 8 }, // subGroups en dur car c'est comme ça dans la BDD du client :-((((
                { label: i18n.rapid, subGroup: 9 },
                { label: i18n.otherType, subGroup: 11 },
            ],
        },
        {
            group: 3,
            title: i18n.depth,
            rows: [
                { label: i18n.depthLess01 },
                { label: i18n.depth01to05 },
                { label: i18n.depth05to1 },
                { label: i18n.depth1to2 },
                { label: i18n.depthMore2 },
            ],
        },
        {
            group: 4,
            title: i18n.currentSpeed,
            rows: [
                { label: i18n.speedLess005 },
                { label: i18n.speed005to02 },
                { label: i18n.speed02to05 },
                { label: i18n.speed05to2 },
                { label: i18n.speedMore2 },
            ],
        },
        {
            group: 5,
            title: i18n.lighting,
            rows: [
                { label: i18n.veryShaded },
                { label: i18n.shaded },
                { label: i18n.littleShaded },
                { label: i18n.lit },
                { label: i18n.veryLit },
            ],
        },
        {
            group: 6,
            title: i18n.substrateType,
            rows: [
                { label: i18n.mudSilt },
                { label: i18n.earthClay },
                { label: i18n.pebbles },
                { label: i18n.blocksSlabs },
                { label: i18n.sandsGravels },
                { label: i18n.rootsBranches },
                { label: i18n.organicDebris },
                { label: i18n.artificial },
            ],
        },
    ]

    const [values, setValues] = useState({})
    const [initialValues, setInitialValues] = useState({})

    const { value: isEditMode, setTrue: onEditMode, setFalse: onReadMode } = useBoolean(false)

    useEffect(() => {
        if (isNil(idStation) || isNil(idOperation) || idOperation === 'new') {
            return
        }
        OperationAction.promiseOperationUnits(idStation, idOperation).then(units => {
            const map = {}
            const list = units || []
            list.forEach(u => {
                map[cellKey(u.group, u.subGroup, u.unit)] = u.result
            })
            setValues(map)
            setInitialValues(map)
        })
    }, [idStation, idOperation])

    const onChangeCell = (key, value) => {
        setValues(prev => {
            const next = { ...prev }
            next[key] = (isNil(value) || value === '') ? undefined : `${value}`
            return next
        })
    }

    const buildUnits = () => keys(values)
        .filter(key => !isNil(values[key]) && values[key] !== '')
        .map(key => {
            const [group, subGroup, unit] = key.split(':').map(Number)
            return { unit, group, subGroup, result: `${values[key]}` }
        })

    useActions(() => {
        if (isEditMode) {
            return {
                save: () => {
                    OperationAction.setOperationUnits(idStation, idOperation, buildUnits()).then(() => {
                        dispatch(ToastrAction.success(i18n.elementUpdateSuccess))
                        setInitialValues(values)
                        onReadMode()
                    })
                },
                cancel: () => {
                    setValues(initialValues)
                    onReadMode()
                },
            }
        }
        return { edit: onEditMode }
    }, [isEditMode, values, initialValues, idStation, idOperation])

    const renderField = (group, subGroup, unit, row) => {
        const key = cellKey(group, subGroup, unit)
        const raw = values[key]
        if (row.sandre) {
            return (
                <Select
                    value={isNil(raw) ? undefined : Number(raw)}
                    options={sandreLists[row.sandre]}
                    keyValue='code'
                    keyLabel='name'
                    integerValue
                    readMode={!isEditMode}
                    onChange={v => onChangeCell(key, v)}
                />
            )
        }
        const numberValue = isNil(raw) ? (isEditMode ? 0 : undefined) : Number(raw)
        return (
            <NumberField
                value={numberValue}
                floatValue
                hideNull
                min={row.percent ? 0 : undefined}
                max={row.percent ? 100 : undefined}
                readMode={!isEditMode}
                onChange={v => onChangeCell(key, v)}
            />
        )
    }

    const buildRows = ({ group, rows }) => rows.map((row, index) => {
        const subGroup = isNil(row.subGroup) ? index : row.subGroup
        return {
            label: row.label,
            ur1: renderField(group, subGroup, UR1, row),
            ur2: renderField(group, subGroup, UR2, row),
        }
    })

    const renderColumn = (title, groups) => (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardTitle title={title} color={darkBlue} centerTitle />
            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {groups.map(group => (
                    <CardTable
                        key={group.group}
                        title={group.title}
                        color={mainBlue}
                        hideNbElements
                        displayHeaders
                        headers={GROUP_HEADERS}
                        rows={buildRows(group)}
                        rowsPerPageOptions={NO_PAGINATION}
                        disableLocalStorage
                    />
                ))}
            </Box>
        </Box>
    )

    return (
        <Grid container spacing={2} sx={{ height: 'calc(100vh - 180px)' }}>
            <Grid size={6} sx={{ height: '100%' }}>
                {renderColumn(i18n.relevedUnits, urGroups)}
            </Grid>
            <Grid size={6} sx={{ height: '100%' }}>
                {renderColumn(i18n.faciesTitle, [faciesGroup])}
            </Grid>
        </Grid>
    )
}

export default HydrobioOperationSurveyUnits